import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import { CircularProgress, useTheme, Link } from '@mui/material';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Alert from '@mui/material/Alert';
import Navbar from './components/Navbar';

function Login() {
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [screenLoading, setScreenLoading] = React.useState(true);
  const googleButtonRef = React.useRef(null);

  const navigate = useNavigate();
  const theme = useTheme();

  React.useEffect(() => {
    const checkIfLoggedIn = async () => {
      const apiUrl = process.env.REACT_APP_BACKEND_API;
      const token = localStorage.getItem('token');
      if (!token) {
        setScreenLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${apiUrl}/user_info`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        if (response.ok) {
          navigate('/dashboard');
        } else {
          localStorage.removeItem('token'); // Remove invalid token
          setScreenLoading(false);
        }
      } catch (error) {
        console.error('Error checking login status:', error);
        localStorage.removeItem('token'); // Remove invalid token
        setScreenLoading(false);
      }
    };

    checkIfLoggedIn();
  }, [navigate]);

  const handleCredentialResponse = React.useCallback(async (response) => {
    setLoading(true);
    setError('');

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_API;
      const result = await fetch(`${apiUrl}/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: response.credential }),
      });

      const data = await result.json();

      if (result.ok) {
        // Store the access token
        localStorage.setItem('token', data.access_token);
        // Navigate to dashboard
        navigate('/dashboard');
      } else {
        setError(data.detail || 'Authentication failed. Please try again.');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error during Google authentication:', error);
      setError('An error occurred. Please try again later.');
      setLoading(false);
    }
  }, [navigate]);

  // Initialize Google Sign-In
  React.useEffect(() => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    
    if (!clientId) {
      console.error('REACT_APP_GOOGLE_CLIENT_ID is not set');
      setError('Google Sign-In is not configured. Please contact support.');
      return;
    }

    let retryCount = 0;
    const maxRetries = 50; // 5 seconds max wait time

    // Wait for Google script to load
    const initializeGoogleSignIn = () => {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        try {
          // Initialize Google Sign-In - disable One Tap and auto-select
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: true,
            itp_support: false,
          });

          // Disable One Tap prompt
          window.google.accounts.id.disableAutoSelect();

          // Render the button after a short delay to ensure the ref is set
          setTimeout(() => {
            const buttonElement = googleButtonRef.current || document.getElementById('google-signin-button');
            if (buttonElement && window.google.accounts.id.renderButton) {
              try {
                // Clear any existing content
                buttonElement.innerHTML = '';
                window.google.accounts.id.renderButton(buttonElement, {
                  theme: 'outline',
                  size: 'large',
                  text: 'signin_with',
                  shape: 'rectangular',
                  logo_alignment: 'left',
                });
                console.log('Google Sign-In button rendered successfully');
              } catch (renderError) {
                console.error('Error rendering Google button:', renderError);
                setError('Failed to render Google Sign-In button. Please refresh the page.');
              }
            } else {
              console.warn('Google button element not available or renderButton not found', {
                ref: googleButtonRef.current,
                element: document.getElementById('google-signin-button'),
                renderButton: !!window.google.accounts.id.renderButton
              });
            }
          }, 200);
        } catch (initError) {
          console.error('Error initializing Google Sign-In:', initError);
          setError('Failed to initialize Google Sign-In. Please refresh the page.');
        }
      } else {
        // Retry after a short delay if Google script hasn't loaded yet
        retryCount++;
        if (retryCount < maxRetries) {
          setTimeout(initializeGoogleSignIn, 100);
        } else {
          console.error('Google Sign-In script failed to load after maximum retries');
          setError('Google Sign-In is taking too long to load. Please refresh the page.');
        }
      }
    };

    // Start initialization
    if (window.google && window.google.accounts && window.google.accounts.id) {
      initializeGoogleSignIn();
    } else {
      // Wait for script to load
      const checkScript = setInterval(() => {
        if (window.google && window.google.accounts && window.google.accounts.id) {
          clearInterval(checkScript);
          initializeGoogleSignIn();
        } else if (retryCount >= maxRetries) {
          clearInterval(checkScript);
          setError('Google Sign-In script failed to load. Please refresh the page.');
        }
        retryCount++;
      }, 100);
    }

    return () => {
      // Cleanup if needed
    };
  }, [handleCredentialResponse]);


  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      backgroundColor: 'background.paper'
    }}>
      {screenLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Header */}
          <Box sx={{ 
            paddingX: { xs: theme.spacing(2), sm: theme.spacing(4) },
            paddingY: theme.spacing(2),
          }}>
            <Navbar />
          </Box>

          {/* Main Content - Centered */}
          <Box sx={{ 
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            paddingX: theme.spacing(2),
            paddingY: theme.spacing(6),
          }}>
            <Container 
              component="main" 
              maxWidth="sm" 
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              {/* Large Logo/Brand */}
              <Typography 
                variant="h3" 
                sx={{ 
                  marginBottom: theme.spacing(1),
                  fontWeight: theme.typography.h3.fontWeight,
                  color: 'text.primary',
                }}
              >
                tenderead.com
              </Typography>

              {/* Main Heading */}
              <Typography 
                variant="h5" 
                sx={{ 
                  marginTop: theme.spacing(4),
                  marginBottom: theme.spacing(1),
                  fontWeight: theme.typography.h5.fontWeight,
                  color: 'text.primary',
                }}
              >
                Log in to your account
              </Typography>

              {/* Instruction Text */}
              <Typography 
                variant="body1" 
                sx={{ 
                  textAlign: "center", 
                  marginBottom: theme.spacing(4),
                  color: 'text.secondary',
                }}
              >
                Use your Google account to continue
              </Typography>

              {/* Error and Loading States */}
              {error && (
                <Alert severity="error" sx={{ mb: theme.spacing(2), width: '100%', maxWidth: '400px' }}>
                  {error}
                </Alert>
              )}
              {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: theme.spacing(2) }}>
                  <CircularProgress />
                </Box>
              )}

              {/* Google Sign-In Button */}
              <Box 
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  marginBottom: theme.spacing(3),
                }}
              >
                <Box 
                  id="google-signin-button"
                  ref={googleButtonRef}
                  sx={{ 
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '40px',
                    '& iframe': {
                      margin: '0 auto !important',
                      display: 'block !important',
                    },
                    '& > div': {
                      margin: '0 auto !important',
                      display: 'flex !important',
                      justifyContent: 'center !important',
                    },
                    '& > div > div': {
                      margin: '0 auto !important',
                    }
                  }}
                />
              </Box>

              {/* Terms and Privacy */}
              <Typography 
                variant="body2" 
                sx={{ 
                  textAlign: "center", 
                  color: 'text.secondary',
                  fontSize: '14px',
                }}
              >
                By continuing, you agree to our{' '}
                <Link href="#" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
                  Terms of Service
                </Link>
                {' '}and{' '}
                <Link href="#" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
                  Privacy Policy
                </Link>
                .
              </Typography>
            </Container>
          </Box>

          {/* Footer */}
          <Box sx={{ 
            paddingX: { xs: theme.spacing(2), sm: theme.spacing(4) },
            paddingY: theme.spacing(3),
            borderTop: `1px solid ${theme.palette.divider}`,
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: theme.spacing(2),
            }}>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '12px',
                }}
              >
                © 2025 tenderead.com. All rights reserved.
              </Typography>
              <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
                <Link 
                  href="#" 
                  sx={{ 
                    color: 'text.secondary',
                    textDecoration: 'none',
                    fontSize: '12px',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Terms of Service
                </Link>
                <Link 
                  href="#" 
                  sx={{ 
                    color: 'text.secondary',
                    textDecoration: 'none',
                    fontSize: '12px',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Privacy Policy
                </Link>
                <Link 
                  href="#" 
                  sx={{ 
                    color: 'text.secondary',
                    textDecoration: 'none',
                    fontSize: '12px',
                    '&:hover': {
                      textDecoration: 'underline',
                    }
                  }}
                >
                  Contact Us
                </Link>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
}

export default Login;
